const API = process.env.REACT_APP_API_BASE_URL;
const RESULT_TYPE = 'ultimo:instagram-oauth-result';
const POLL_MS = 2000;
const TIMEOUT_MS = 10 * 60 * 1000;
const AUTH_ORIGINS = new Set(['https://www.instagram.com', 'https://instagram.com', 'https://www.facebook.com', 'https://facebook.com']);

export const hasInstagramConnection = data =>
  Array.isArray(data?.connections) && data.connections.some(item => item.status === 'active');

export function instagramOAuthResult(search) {
  const params = new URLSearchParams(search);
  // Provider results are separate from Wix authentication, even on the same route.
  if (['instance', 'signedInstance', 'code', 'state', 'token'].some(key => params.has(key))) return null;
  if (params.has('instagram_error')) return params.get('instagram_error') === 'cancelled' ? 'cancelled' : 'error';
  return params.get('connected') === 'instagram' ? 'success' : null;
}

export function notifyInstagramOpener(result) {
  if (!window.opener) return false;
  window.opener.postMessage({type:RESULT_TYPE, result}, window.location.origin);
  return true;
}

/** Start from the click handler, before any await, so browsers allow the popup.
 * The original frame keeps its session; only its authenticated status API can
 * confirm a connection. No storage sharing or credentials in messages is needed.
 */
export function startInstagramOAuth({botId, returnPath, openingLabel, onStatus, onError, onDone}) {
  let popup;
  try {
    popup = window.open('about:blank', '_blank', 'popup,width=600,height=750,scrollbars=yes,resizable=yes');
  } catch { /* A sandbox or popup blocker may reject window.open. */ }
  if (!popup) {
    onError('wix_instagram_popup_blocked'); onDone();
    return () => {};
  }
  try {
    popup.document.title = 'Instagram';
    popup.document.body.textContent = openingLabel;
  } catch { /* The browser may isolate the new browsing context. */ }

  const controller = new AbortController();
  let stopped = false, timer, expiryTimer, checking = null, result = null;
  const closePopup = () => { try { popup.close(); } catch { /* May be isolated by provider COOP. */ } };
  const cleanup = () => {
    stopped = true;
    clearTimeout(timer);
    clearTimeout(expiryTimer);
    controller.abort();
    window.removeEventListener('message', onMessage);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisibility);
    closePopup();
  };
  const finish = error => {
    if (stopped) return;
    cleanup();
    if (error) onError(error);
    onDone();
  };
  const check = () => {
    if (stopped) return Promise.resolve();
    if (checking) return checking;
    checking = (async () => {
      try {
        const response = await fetch(`${API}auth/instagram/status?bot_id=${encodeURIComponent(botId)}`, {signal:controller.signal});
        if (!response.ok) throw new Error('status unavailable');
        const data = await response.json();
        if (stopped) return;
        onStatus(data);
        if (hasInstagramConnection(data)) finish();
        else if (result === 'cancelled' || result === 'error') finish(result === 'cancelled' ? 'wix_instagram_cancelled' : 'wix_instagram_connect_error');
      } catch {
        // A temporary status failure must not interrupt provider authorization.
        if (result === 'cancelled' || result === 'error') finish(result === 'cancelled' ? 'wix_instagram_cancelled' : 'wix_instagram_connect_error');
      }
    })().finally(() => { checking = null; });
    return checking;
  };
  function onMessage(event) {
    if (event.origin !== window.location.origin || event.source !== popup || event.data?.type !== RESULT_TYPE
        || !['success', 'cancelled', 'error'].includes(event.data.result)) return;
    result = event.data.result;
    check();
  }
  function onFocus() { check(); }
  function onVisibility() { if (document.visibilityState === 'visible') check(); }
  async function poll() {
    if (stopped) return;
    await check();
    // Do not infer cancellation from popup.closed: COOP can sever the handle
    // while Instagram is still open. Polling also works without an opener.
    if (!stopped) timer = setTimeout(poll, POLL_MS);
  }
  window.addEventListener('message', onMessage);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);
  expiryTimer = setTimeout(() => finish('wix_instagram_connect_timeout'), TIMEOUT_MS);
  (async () => {
    try {
      const params = new URLSearchParams({bot_id:botId, auto_response_mode:'respond_all', return_to:returnPath});
      const response = await fetch(`${API}auth/instagram/connect?${params}`, {signal:controller.signal});
      const data = await response.json();
      if (stopped) return;
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'wix_instagram_connect_error');
      let target;
      try { target = new URL(data.authorizeUrl); } catch { throw new Error('wix_instagram_connect_error'); }
      if (!AUTH_ORIGINS.has(target.origin) || target.username || target.password) throw new Error('wix_instagram_connect_error');
      popup.location.replace(target.href);
      timer = setTimeout(poll, POLL_MS);
    } catch (error) {
      if (!stopped) finish(error.message || 'wix_instagram_connect_error');
    }
  })();
  return cleanup;
}
